import React, { useEffect, useMemo, useRef, useState } from "react";
import { TL } from "../i18n";
import client from "../lib/api";
import { Button, Badge } from "./ui";
import { toast } from "sonner";
import { n, fmt } from "../lib/equityMath";
import { Plus, Trash2, TrendingUp, TrendingDown, Search } from "lucide-react";
import { AppraisalButton } from "./Appraisal";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";

const PERIODS = [["6h", "6h"], ["12h", "12h"], ["1d", "Day"], ["1w", "Week"], ["1m", "Month"], ["90d", "90 day"], ["180d", "180 day"], ["1y", "1 year"]];
const DEFAULTS = [
  { symbol: "BTC", name: "Bitcoin" }, { symbol: "ETH", name: "Ethereum" }, { symbol: "USDT", name: "Tether" },
  { symbol: "", name: "" }, { symbol: "", name: "" },
];
const COLORS = ["#F7931A", "#627EEA", "#26A17B", "#8B5CF6", "#EC4899", "#10B981", "#F59E0B"];
const II = "w-full rounded border border-black/15 bg-white/70 px-1.5 py-1 text-[11px] tabular-nums dark:border-white/15 dark:bg-black/30";

export default function CryptoLedger({ notes = [], reload, scope = null }) {
  const [mode, setMode] = useState("coins");
  const nftItems = useMemo(() => notes.filter((x) => x.kind === "nft"), [notes]);
  const coins = useMemo(() => {
    const order = DEFAULTS.map((d) => d.symbol).filter(Boolean);
    const rank = (s) => { const i = order.indexOf((s || "").toUpperCase()); return i === -1 ? 999 : i; };
    return notes.filter((x) => x.kind !== "nft").sort((a, b) => (b.symbol ? 1 : 0) - (a.symbol ? 1 : 0) || rank(a.symbol) - rank(b.symbol));
  }, [notes]);
  const [prices, setPrices] = useState({});
  const [trades, setTrades] = useState([]);
  const [hist, setHist] = useState({}); // { "SYM:period": {low, high} }
  const [lowP, setLowP] = useState({}); // id -> period
  const [highP, setHighP] = useState({});
  const [graphP, setGraphP] = useState("1w");
  const [graphData, setGraphData] = useState({ rows: [], syms: [] });
  const [tradeFor, setTradeFor] = useState(null);
  const seeded = useRef(false);

  // ---- auto-create the 5 defaults on first open ----
  useEffect(() => {
    if (seeded.current) return;
    if (scope) { seeded.current = true; return; } // scoped (e.g. Alternative Assets) — no auto-seed
    if (coins.length === 0) {
      seeded.current = true;
      (async () => {
        for (const d of DEFAULTS) await client.post("/holdings", { data: { category: "Cryptocurrencies", subcategory: "Coins", asset_class: "Crypto", symbol: d.symbol, name: d.name, product: d.symbol ? `${d.symbol}-USD` : "" } });
        reload && reload();
      })();
    }
  }, [coins.length, reload]);

  const loadTrades = () => client.get("/crypto_trades").then((r) => setTrades((r.data || []).map((x) => ({ id: x.id, ...(x.data || x) })))).catch(() => {});
  useEffect(() => { loadTrades(); }, []);

  const symbols = useMemo(() => [...new Set(coins.map((c) => (c.symbol || "").toUpperCase()).filter(Boolean))], [coins]);
  const loadPrices = () => {
    if (!symbols.length) return;
    client.get(`/crypto/prices?symbols=${symbols.join(",")}`).then((r) => setPrices(r.data || {})).catch(() => {});
  };
  useEffect(() => { loadPrices(); const t = setInterval(loadPrices, 60000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [symbols.join(",")]);

  const fetchHist = async (sym, period) => {
    if (!sym) return;
    const key = `${sym}:${period}`;
    if (hist[key]) return;
    try {
      const r = await client.get(`/crypto/history?symbol=${sym}&period=${period}`);
      setHist((p) => ({ ...p, [key]: { low: r.data.low, high: r.data.high, series: r.data.series } }));
    } catch (e) { /* ignore */ }
  };
  // load default 1d low/high for each coin
  useEffect(() => { symbols.forEach((s) => fetchHist(s, "1d")); /* eslint-disable-next-line */ }, [symbols.join(",")]);

  // ---- graph ----
  useEffect(() => {
    (async () => {
      const syms = symbols.slice(0, 7);
      const seriesBySym = {};
      for (const s of syms) {
        try {
          const r = await client.get(`/crypto/history?symbol=${s}&period=${graphP}`);
          seriesBySym[s] = r.data.series || [];
        } catch (e) { seriesBySym[s] = []; }
      }
      const maxLen = Math.max(0, ...syms.map((s) => (seriesBySym[s] || []).length));
      const rows = [];
      for (let i = 0; i < maxLen; i++) {
        const row = {};
        let t = null;
        syms.forEach((s) => {
          const ser = seriesBySym[s] || [];
          const pt = ser[i];
          if (pt) {
            if (!t) t = pt[0];
            const base = ser[0] ? ser[0][1] : pt[1];
            row[s] = base ? +(((pt[1] / base) - 1) * 100).toFixed(2) : 0;
          }
        });
        row.t = t;
        rows.push(row);
      }
      setGraphData({ rows, syms });
    })();
    /* eslint-disable-next-line */
  }, [symbols.join(","), graphP]);

  // ---- per-coin computed ----
  const tradesFor = (id) => trades.filter((t) => t.position_id === id);
  const held = (id) => tradesFor(id).reduce((a, t) => a + (t.side === "buy" ? n(t.units) : -n(t.units)), 0);
  const bought = (id) => tradesFor(id).filter((t) => t.side === "buy").reduce((a, t) => a + n(t.units) * n(t.price), 0);
  const sold = (id) => tradesFor(id).filter((t) => t.side === "sell").reduce((a, t) => a + n(t.units) * n(t.price), 0);
  const lastP = (sym) => prices[(sym || "").toUpperCase()]?.usd ?? null;

  const setField = (id, key, value, persist) => {
    if (persist) client.put(`/holdings/${id}`, { data: { [key]: value } }).catch(() => {});
  };
  const setSymbol = (id, sym) => {
    const s = (sym || "").toUpperCase();
    client.put(`/holdings/${id}`, { data: { symbol: s, product: s ? `${s}-USD` : "" } }).then(() => reload && reload()).catch(() => {});
  };

  const addCoin = async () => {
    await client.post("/holdings", { data: { category: "Cryptocurrencies", subcategory: "Coins", asset_class: "Crypto", symbol: "", name: "", product: "", ...(scope || {}) } });
    reload && reload();
  };
  const delCoin = async (c) => {
    if (!window.confirm(`Remove ${c.symbol || c.name || "this coin"} and its logged trades?`)) return;
    await client.delete(`/holdings/${c.id}`);
    await Promise.all(tradesFor(c.id).map((t) => client.delete(`/crypto_trades/${t.id}`).catch(() => {})));
    loadTrades(); reload && reload();
  };

  // ---- portfolio summary ----
  const summary = useMemo(() => {
    let totalVOH = 0, totalGL = 0, weightedChg = 0, changeBase = 0, heldCount = 0;
    coins.forEach((c) => {
      const sym = (c.symbol || "").toUpperCase();
      const h = held(c.id), last = lastP(sym), vOH = h * (last || 0);
      totalVOH += vOH;
      totalGL += vOH + sold(c.id) - bought(c.id);
      if (h > 0) heldCount += 1;
      const chg = prices[sym]?.change24h;
      if (vOH > 0 && chg != null) { weightedChg += vOH * chg; changeBase += vOH; }
    });
    return { totalVOH, totalGL, avgChg: changeBase ? weightedChg / changeBase : null, heldCount };
  }, [coins, trades, prices]); // eslint-disable-line

  return (
    <div className="space-y-4" data-testid="crypto-ledger">
      {/* ===== Coins / NFTs toggle ===== */}
      <div className="inline-flex overflow-hidden rounded-lg border border-black/15 text-[11px] dark:border-white/15" data-testid="crypto-mode-toggle">
        {[["coins", "Coins"], ["nfts", "NFTs"]].map(([m, l]) => (
          <button key={m} onClick={() => setMode(m)} data-testid={`crypto-mode-${m}`} className={`px-3 py-1 font-semibold ${mode === m ? "bg-[var(--primary)] text-white" : "hover:bg-black/5 dark:hover:bg-white/10"}`}>{l}{m === "nfts" && nftItems.length ? ` (${nftItems.length})` : ""}</button>
        ))}
      </div>

      {mode === "nfts" ? <NftLedger notes={nftItems} reload={reload} scope={scope} /> : (<>
      {/* ===== Portfolio summary ===== */}
      <div className="grid gap-3 sm:grid-cols-4" data-testid="crypto-summary">
        <div className="rounded-lg border border-black/10 bg-black/[0.02] p-3 dark:border-white/10 dark:bg-white/[0.03]">
          <div className="text-[9px] uppercase tracking-wide opacity-55">{TL("Total value on hand")}</div>
          <div className="mt-0.5 text-lg font-semibold tabular-nums" data-testid="crypto-summary-voh">${fmt(summary.totalVOH)}</div>
        </div>
        <div className="rounded-lg border border-black/10 bg-black/[0.02] p-3 dark:border-white/10 dark:bg-white/[0.03]">
          <div className="text-[9px] uppercase tracking-wide opacity-55">{TL("24h change")}</div>
          <div className={`mt-0.5 flex items-center gap-1 text-lg font-semibold tabular-nums ${summary.avgChg == null ? "opacity-40" : summary.avgChg >= 0 ? "text-emerald-600" : "text-red-500"}`} data-testid="crypto-summary-change">
            {summary.avgChg == null ? "—" : <>{summary.avgChg >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}{summary.avgChg >= 0 ? "+" : ""}{summary.avgChg.toFixed(2)}%</>}
          </div>
        </div>
        <div className="rounded-lg border border-black/10 bg-black/[0.02] p-3 dark:border-white/10 dark:bg-white/[0.03]">
          <div className="text-[9px] uppercase tracking-wide opacity-55">{TL("Total gain / loss")}</div>
          <div className={`mt-0.5 text-lg font-semibold tabular-nums ${summary.totalGL >= 0 ? "text-emerald-600" : "text-red-500"}`} data-testid="crypto-summary-gl">{summary.totalGL >= 0 ? "+" : ""}${fmt(summary.totalGL)}</div>
        </div>
        <div className="rounded-lg border border-black/10 bg-black/[0.02] p-3 dark:border-white/10 dark:bg-white/[0.03]">
          <div className="text-[9px] uppercase tracking-wide opacity-55">{TL("Coins held")}</div>
          <div className="mt-0.5 text-lg font-semibold tabular-nums" data-testid="crypto-summary-count">{summary.heldCount}</div>
        </div>
      </div>

      {/* ===== Coin table ===== */}
      <div className="overflow-x-auto rounded-lg border border-black/10 dark:border-white/10">
        <table className="w-full min-w-[1050px] text-[11px]">
          <thead className="bg-black/[0.03] text-[9px] uppercase tracking-wide opacity-70 dark:bg-white/[0.04]">
            <tr>
              <th className="px-2 py-1.5 text-start">{TL("Currency")}</th>
              <th className="px-2 py-1.5 text-end">{TL("# held")}</th>
              <th className="px-2 py-1.5 text-end">{TL("Last value")}</th>
              <th className="px-2 py-1.5 text-end">{TL("Bought")}</th>
              <th className="px-2 py-1.5 text-end">{TL("Sold")}</th>
              <th className="px-2 py-1.5 text-end">{TL("Gain / loss")}</th>
              <th className="px-2 py-1.5 text-start">{TL("Low")}</th>
              <th className="px-2 py-1.5 text-start">{TL("High")}</th>
              <th className="px-2 py-1.5 text-end">{TL("Value on hand")}</th>
              <th className="px-2 py-1.5"></th>
            </tr>
          </thead>
          <tbody>
            {coins.map((c, idx) => {
              const sym = (c.symbol || "").toUpperCase();
              const h = held(c.id), last = lastP(sym), vOH = h * (last || 0), gl = vOH + sold(c.id) - bought(c.id);
              const lp = lowP[c.id] || "1d", hp = highP[c.id] || "1d";
              const lowV = hist[`${sym}:${lp}`]?.low, highV = hist[`${sym}:${hp}`]?.high;
              const chg = prices[sym]?.change24h;
              return (
                <tr key={c.id} className="border-t border-black/5 dark:border-white/5" data-testid={`crypto-row-${c.id}`}>
                  <td className="px-2 py-1.5">
                    <div className="flex items-center gap-1">
                      <span className="inline-block h-2 w-2 rounded-full" style={{ background: COLORS[idx % COLORS.length] }} />
                      <CoinPicker c={c} onSet={(s, nm) => { setSymbol(c.id, s); if (nm) setField(c.id, "name", nm, true); }} />
                    </div>
                  </td>
                  <td className="px-2 py-1.5 text-end tabular-nums" data-testid={`crypto-held-${c.id}`}>{fmt(h)}</td>
                  <td className="px-2 py-1.5 text-end tabular-nums">
                    {last != null ? <span>${fmt(last)}</span> : <span className="opacity-40">—</span>}
                    {chg != null && <span className={`ms-1 text-[9px] ${chg >= 0 ? "text-emerald-600" : "text-red-500"}`}>{chg >= 0 ? "▲" : "▼"}{Math.abs(chg).toFixed(1)}%</span>}
                  </td>
                  <td className="px-2 py-1.5 text-end tabular-nums">${fmt(bought(c.id))}</td>
                  <td className="px-2 py-1.5 text-end tabular-nums">${fmt(sold(c.id))}</td>
                  <td className={`px-2 py-1.5 text-end tabular-nums font-semibold ${gl >= 0 ? "text-emerald-600" : "text-red-500"}`} data-testid={`crypto-gl-${c.id}`}>{gl >= 0 ? "+" : ""}${fmt(gl)}</td>
                  <td className="px-2 py-1.5">
                    <PeriodPick value={lp} onChange={(v) => { setLowP((p) => ({ ...p, [c.id]: v })); fetchHist(sym, v); }} tid={`crypto-low-period-${c.id}`} />
                    <div className="text-[10px] tabular-nums" data-testid={`crypto-low-${c.id}`}>{lowV != null ? `$${fmt(lowV)}` : "…"}</div>
                  </td>
                  <td className="px-2 py-1.5">
                    <PeriodPick value={hp} onChange={(v) => { setHighP((p) => ({ ...p, [c.id]: v })); fetchHist(sym, v); }} tid={`crypto-high-period-${c.id}`} />
                    <div className="text-[10px] tabular-nums" data-testid={`crypto-high-${c.id}`}>{highV != null ? `$${fmt(highV)}` : "…"}</div>
                  </td>
                  <td className="px-2 py-1.5 text-end tabular-nums font-semibold" data-testid={`crypto-voh-${c.id}`}>${fmt(vOH)}</td>
                  <td className="px-2 py-1.5">
                    <div className="flex items-center gap-1">
                      <button onClick={() => setTradeFor(tradeFor === c.id ? null : c.id)} data-testid={`crypto-trade-btn-${c.id}`} className="rounded border border-black/15 px-1.5 py-0.5 text-[10px] hover:bg-[var(--primary)] hover:text-white dark:border-white/15" disabled={!sym}>{TL("Trade")}</button>
                      <AppraisalButton holding={c} reload={reload} />
                      <button onClick={() => delCoin(c)} data-testid={`crypto-del-${c.id}`} className="opacity-50 hover:text-red-500 hover:opacity-100"><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={addCoin} data-testid="crypto-add-coin"><Plus size={14} /> {TL("Add currency")}</Button>
        {tradeFor && <TradePanel coin={coins.find((c) => c.id === tradeFor)} last={lastP((coins.find((c) => c.id === tradeFor)?.symbol) || "")} onClose={() => setTradeFor(null)} onSaved={() => { loadTrades(); setTradeFor(null); }} />}
      </div>

      {/* ===== Graph + Exchange dashes ===== */}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="lg:order-2 rounded-lg border border-black/10 p-3 dark:border-white/10" data-testid="crypto-graph">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-[11px] font-semibold">{TL("Price movement (% change)")}</div>
            <PeriodPick value={graphP} onChange={setGraphP} tid="crypto-graph-period" />
          </div>
          <div style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={graphData.rows} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                <XAxis dataKey="t" tick={false} axisLine={false} />
                <YAxis tick={{ fontSize: 9 }} tickFormatter={(v) => `${v}%`} />
                <Tooltip formatter={(v, k) => [`${v}%`, k]} labelFormatter={() => ""} contentStyle={{ fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                {graphData.syms.map((s, i) => <Line key={s} type="monotone" dataKey={s} stroke={COLORS[i % COLORS.length]} dot={false} strokeWidth={1.6} isAnimationActive={false} />)}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="lg:order-1 grid gap-3 sm:grid-cols-2">
          <ExchangeWallet coins={coins} prices={prices} onRun={loadTrades} />
          <ExchangeFiat coins={coins} prices={prices} onRun={loadTrades} />
        </div>
      </div>
      </>)}
    </div>
  );
}

function PeriodPick({ value, onChange, tid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={tid} className="rounded border border-black/15 bg-white/70 px-1 py-0.5 text-[10px] dark:border-white/15 dark:bg-black/30">
      {PERIODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
    </select>
  );
}

function CoinPicker({ c, onSet }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [res, setRes] = useState([]);
  useEffect(() => {
    if (!open || q.length < 1) { setRes([]); return; }
    const t = setTimeout(() => client.get(`/crypto/search?q=${encodeURIComponent(q)}`).then((r) => setRes(r.data.coins || [])).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [q, open]);
  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)} data-testid={`crypto-coinpick-${c.id}`} className="flex items-center gap-1 rounded px-1 py-0.5 text-start hover:bg-black/5 dark:hover:bg-white/10">
        <span className="font-semibold">{c.symbol || "＋ set"}</span>
        {c.name && c.name.toUpperCase() !== (c.symbol || "").toUpperCase() && <span className="text-[10px] opacity-60">{c.name}</span>}
        <Search size={10} className="opacity-40" />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-56 rounded-lg border border-black/15 bg-white p-2 shadow-lg dark:border-white/15 dark:bg-neutral-900">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={TL("Search coin (BTC, sol…)")} data-testid={`crypto-search-input-${c.id}`} className={II} />
          <div className="mt-1 max-h-40 overflow-y-auto">
            {res.map((r) => (
              <button key={r.product} onClick={() => { onSet(r.symbol, r.name); setOpen(false); setQ(""); }} data-testid={`crypto-search-opt-${r.symbol}`} className="flex w-full items-center justify-between rounded px-2 py-1 text-start text-[11px] hover:bg-black/5 dark:hover:bg-white/10">
                <span className="font-semibold">{r.symbol}</span><span className="opacity-60">{r.name}</span>
              </button>
            ))}
            {q.length >= 1 && res.length === 0 && <div className="px-2 py-1 text-[10px] opacity-50">{TL("No USD-traded match.")}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function TradePanel({ coin, last, onClose, onSaved }) {
  const [side, setSide] = useState("buy");
  const [units, setUnits] = useState("");
  const [price, setPrice] = useState(last ? String(last) : "");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const save = async () => {
    if (!(n(units) > 0) || !(n(price) > 0)) return toast.error("Enter units and price.");
    try {
      await client.post("/crypto_trades", { data: { position_id: coin.id, side, units: n(units), price: n(price), date } });
      toast.success(`${side === "buy" ? "Bought" : "Sold"} ${units} ${coin.symbol}.`);
      onSaved();
    } catch (e) { toast.error("Could not save trade."); }
  };
  return (
    <div className="flex flex-wrap items-end gap-2 rounded-lg border border-[var(--primary)]/40 bg-[var(--primary)]/5 p-2" data-testid="crypto-trade-panel">
      <span className="text-[11px] font-semibold">{coin.symbol} trade</span>
      <select value={side} onChange={(e) => setSide(e.target.value)} data-testid="crypto-trade-side" className="rounded border border-black/15 px-1.5 py-1 text-[11px] dark:border-white/15 dark:bg-black/30"><option value="buy">{TL("Buy")}</option><option value="sell">{TL("Sell")}</option></select>
      <input type="number" value={units} onChange={(e) => setUnits(e.target.value)} placeholder={TL("units")} data-testid="crypto-trade-units" className={`${II} w-24`} />
      <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="price $" data-testid="crypto-trade-price" className={`${II} w-28`} />
      <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="crypto-trade-date" className={`${II} w-32`} />
      <Button onClick={save} data-testid="crypto-trade-save">{TL("Save")}</Button>
      <button onClick={onClose} className="text-[11px] opacity-60 hover:opacity-100">{TL("Cancel")}</button>
    </div>
  );
}

function ExchangeWallet({ coins, prices, onRun }) {
  const opts = coins.filter((c) => c.symbol);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [amt, setAmt] = useState("");
  const [out, setOut] = useState(null);
  const price = (id) => { const c = coins.find((x) => x.id === id); return c ? prices[(c.symbol || "").toUpperCase()]?.usd : null; };
  const calc = () => {
    const pf = price(from), pt = price(to);
    if (!pf || !pt || !(n(amt) > 0)) { setOut(null); return toast.error("Pick both coins and an amount."); }
    setOut(n(amt) * pf / pt);
  };
  const run = async () => {
    const pf = price(from), pt = price(to);
    if (out == null || !pf || !pt) return toast.error("Calculate first.");
    const d = new Date().toISOString().slice(0, 10);
    try {
      await client.post("/crypto_trades", { data: { position_id: from, side: "sell", units: n(amt), price: pf, date: d } });
      await client.post("/crypto_trades", { data: { position_id: to, side: "buy", units: out, price: pt, date: d } });
      toast.success(`Exchanged ${amt} ${coins.find((c) => c.id === from)?.symbol} → ${fmt(out)} ${coins.find((c) => c.id === to)?.symbol}.`);
      onRun();
    } catch (e) { toast.error("Run failed."); }
  };
  return (
    <div className="rounded-lg border border-black/10 p-3 dark:border-white/10" data-testid="crypto-exchange-wallet">
      <div className="text-[11px] font-semibold">{TL("Exchange in Wallet")} <span className="opacity-50">{TL("(crypto → crypto)")}</span></div>
      <div className="mt-2 space-y-1.5">
        <SelCoin label={TL("From")} value={from} onChange={setFrom} opts={opts} tid="cxw-from" />
        <label className="block"><span className="text-[9px] uppercase opacity-55">{TL("Target amount")}</span><input type="number" value={amt} onChange={(e) => setAmt(e.target.value)} data-testid="cxw-amount" className={II} /></label>
        <SelCoin label={TL("To")} value={to} onChange={setTo} opts={opts} tid="cxw-to" />
      </div>
      <div className="mt-2 flex items-center gap-2">
        <Button variant="ghost" onClick={calc} data-testid="cxw-calc">{TL("Calculate")}</Button>
        <div className="flex-1 text-[11px] tabular-nums" data-testid="cxw-result">{out != null ? `= ${fmt(out)} ${coins.find((c) => c.id === to)?.symbol || ""}` : ""}</div>
        <Button onClick={run} data-testid="cxw-run">{TL("Run")}</Button>
      </div>
    </div>
  );
}

function ExchangeFiat({ coins, prices, onRun }) {
  const priceOf = (sym) => prices[(sym || "").toUpperCase()]?.usd;
  const opts = [{ id: "USD", symbol: "USD", fiat: true }, ...coins.filter((c) => c.symbol).map((c) => ({ id: c.id, symbol: c.symbol }))];
  const [from, setFrom] = useState("USD");
  const [to, setTo] = useState("");
  const [amt, setAmt] = useState("");
  const [out, setOut] = useState(null);
  const usdVal = (sel, a) => sel === "USD" ? a : a * (priceOf(coins.find((c) => c.id === sel)?.symbol) || 0);
  const calc = () => {
    if (!(n(amt) > 0) || !from || !to) { setOut(null); return toast.error("Pick from/to and amount."); }
    const usd = usdVal(from, n(amt));
    const res = to === "USD" ? usd : usd / (priceOf(coins.find((c) => c.id === to)?.symbol) || 1);
    setOut(res);
  };
  const run = async () => {
    if (out == null) return toast.error("Calculate first.");
    const d = new Date().toISOString().slice(0, 10);
    try {
      if (from !== "USD") { const c = coins.find((x) => x.id === from); await client.post("/crypto_trades", { data: { position_id: from, side: "sell", units: n(amt), price: priceOf(c?.symbol), date: d } }); }
      if (to !== "USD") { const c = coins.find((x) => x.id === to); await client.post("/crypto_trades", { data: { position_id: to, side: "buy", units: out, price: priceOf(c?.symbol), date: d } }); }
      toast.success("Fiat exchange recorded.");
      onRun();
    } catch (e) { toast.error("Run failed."); }
  };
  const label = (sel) => opts.find((o) => o.id === sel)?.symbol || "";
  return (
    <div className="rounded-lg border border-black/10 p-3 dark:border-white/10" data-testid="crypto-exchange-fiat">
      <div className="text-[11px] font-semibold">{TL("Exchange in Fiat")} <span className="opacity-50">{TL("(crypto ↔ USD)")}</span></div>
      <div className="mt-2 space-y-1.5">
        <SelCoin label={TL("From")} value={from} onChange={setFrom} opts={opts} tid="cxf-from" />
        <label className="block"><span className="text-[9px] uppercase opacity-55">{TL("Target amount")}</span><input type="number" value={amt} onChange={(e) => setAmt(e.target.value)} data-testid="cxf-amount" className={II} /></label>
        <SelCoin label={TL("To")} value={to} onChange={setTo} opts={opts} tid="cxf-to" />
      </div>
      <div className="mt-2 flex items-center gap-2">
        <Button variant="ghost" onClick={calc} data-testid="cxf-calc">{TL("Calculate")}</Button>
        <div className="flex-1 text-[11px] tabular-nums" data-testid="cxf-result">{out != null ? `= ${fmt(out)} ${label(to)}` : ""}</div>
        <Button onClick={run} data-testid="cxf-run">{TL("Run")}</Button>
      </div>
    </div>
  );
}

function SelCoin({ label, value, onChange, opts, tid }) {
  return (
    <label className="block"><span className="text-[9px] uppercase opacity-55">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={tid} className={II}>
        <option value="">{TL("— select —")}</option>
        {opts.map((o) => <option key={o.id} value={o.id}>{o.symbol}</option>)}
      </select>
    </label>
  );
}

function NftLedger({ notes = [], reload, scope = null }) {
  const [edits, setEdits] = useState({});
  useEffect(() => {
    setEdits((prev) => { const ids = new Set(notes.map((i) => i.id)); const next = {}; Object.keys(prev).forEach((k) => { if (ids.has(k)) next[k] = prev[k]; }); return next; });
  }, [notes]);
  const view = (r) => ({ ...r, ...(edits[r.id] || {}) });
  const setField = (id, k, v) => setEdits((p) => ({ ...p, [id]: { ...(p[id] || {}), [k]: v } }));
  const persist = (id, k) => client.put(`/holdings/${id}`, { data: { [k]: (edits[id] || {})[k] } }).catch(() => toast.error("Could not save."));
  const add = async (count) => {
    for (let i = 0; i < count; i++) await client.post("/holdings", { data: { category: "Cryptocurrencies", subcategory: "NFTs", asset_class: "Crypto", kind: "nft", name: "", nft_collection: "", nft_units: "1", nft_cost: "", nft_value: "", ...(scope || {}) } });
    toast.success(`Added ${count} NFT slot${count > 1 ? "s" : ""}.`); reload && reload();
  };
  const del = async (r) => { if (!window.confirm(`Remove ${r.name || "this NFT"}?`)) return; await client.delete(`/holdings/${r.id}`); toast.success("Removed."); reload && reload(); };
  const voh = (r) => n(r.nft_units) * n(r.nft_value);
  const cost = (r) => n(r.nft_units) * n(r.nft_cost);
  const gl = (r) => voh(r) - cost(r);
  const totals = useMemo(() => notes.reduce((a, raw) => { const r = view(raw); a.voh += voh(r); a.gl += gl(r); return a; }, { voh: 0, gl: 0 }), [notes, edits]); // eslint-disable-line

  return (
    <div className="space-y-3" data-testid="nft-ledger">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] uppercase tracking-wide opacity-55">{TL("Add NFT:")}</span>
        <div className="flex overflow-hidden rounded border border-black/15 text-[11px] dark:border-white/15">
          {[1, 5, 10].map((c) => <button key={c} onClick={() => add(c)} data-testid={`nft-add-${c}`} className="px-2 py-1 font-semibold hover:bg-[var(--primary)] hover:text-white">+{c}</button>)}
        </div>
        <div className="ms-auto flex items-center gap-4 text-[11px]">
          <span>{TL("Value on hand")} <b className="tabular-nums" data-testid="nft-total-voh">${fmt(totals.voh)}</b></span>
          <span>{TL("Gain / loss")} <b className={`tabular-nums ${totals.gl >= 0 ? "text-emerald-600" : "text-red-500"}`} data-testid="nft-total-gl">{totals.gl >= 0 ? "+" : ""}${fmt(totals.gl)}</b></span>
        </div>
      </div>
      {notes.length === 0 ? (
        <div className="rounded-lg border border-dashed border-black/15 py-6 text-center text-xs opacity-60 dark:border-white/15" data-testid="nft-empty">{TL("No NFTs yet. Use “+1 / +5 / +10” to add rows — every field is renamable.")}</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-black/10 dark:border-white/10">
          <table className="w-full min-w-[820px] text-[11px]">
            <thead className="bg-black/[0.03] text-[9px] uppercase tracking-wide opacity-70 dark:bg-white/[0.04]">
              <tr><th className="px-2 py-1.5 text-start">{TL("Name")}</th><th className="px-2 py-1.5 text-start">{TL("Collection")}</th><th className="px-2 py-1.5 text-end">{TL("Units")}</th><th className="px-2 py-1.5 text-end">{TL("Unit cost")}</th><th className="px-2 py-1.5 text-end">{TL("Unit value")}</th><th className="px-2 py-1.5 text-end">{TL("Value on hand")}</th><th className="px-2 py-1.5 text-end">{TL("Gain / loss")}</th><th className="px-2 py-1.5"></th></tr>
            </thead>
            <tbody>
              {notes.map((raw) => { const r = view(raw); return (
                <tr key={r.id} className="border-t border-black/5 dark:border-white/5" data-testid={`nft-row-${r.id}`}>
                  <td className="px-2 py-1"><input className={II} value={r.name || ""} onChange={(e) => setField(r.id, "name", e.target.value)} onBlur={() => persist(r.id, "name")} placeholder={TL("Rename…")} data-testid={`nft-name-${r.id}`} /></td>
                  <td className="px-2 py-1"><input className={II} value={r.nft_collection || ""} onChange={(e) => setField(r.id, "nft_collection", e.target.value)} onBlur={() => persist(r.id, "nft_collection")} placeholder={TL("Collection")} data-testid={`nft-collection-${r.id}`} /></td>
                  <td className="px-2 py-1"><input type="number" className={`${II} w-16 text-end`} value={r.nft_units || ""} onChange={(e) => setField(r.id, "nft_units", e.target.value)} onBlur={() => persist(r.id, "nft_units")} data-testid={`nft-units-${r.id}`} /></td>
                  <td className="px-2 py-1"><input type="number" className={`${II} w-24 text-end`} value={r.nft_cost || ""} onChange={(e) => setField(r.id, "nft_cost", e.target.value)} onBlur={() => persist(r.id, "nft_cost")} data-testid={`nft-cost-${r.id}`} /></td>
                  <td className="px-2 py-1"><input type="number" className={`${II} w-24 text-end`} value={r.nft_value || ""} onChange={(e) => setField(r.id, "nft_value", e.target.value)} onBlur={() => persist(r.id, "nft_value")} data-testid={`nft-value-${r.id}`} /></td>
                  <td className="px-2 py-1.5 text-end tabular-nums font-semibold" data-testid={`nft-voh-${r.id}`}>${fmt(voh(r))}</td>
                  <td className={`px-2 py-1.5 text-end tabular-nums font-semibold ${gl(r) >= 0 ? "text-emerald-600" : "text-red-500"}`} data-testid={`nft-gl-${r.id}`}>{gl(r) >= 0 ? "+" : ""}${fmt(gl(r))}</td>
                  <td className="px-2 py-1.5"><button onClick={() => del(raw)} data-testid={`nft-del-${r.id}`} className="opacity-50 hover:text-red-500 hover:opacity-100"><Trash2 size={12} /></button></td>
                </tr>
              ); })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
